import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import type { Persona, TrackContext } from '../types';

export function usePersonas() {
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPersonas = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const result = await api.getPersonas();

    if (result.error) {
      setError(result.error);
    } else {
      setPersonas(result.data?.personas || []);
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchPersonas();
  }, [fetchPersonas]);

  return { personas, isLoading, error, refetch: fetchPersonas };
}

export function usePersona(id: string) {
  const [persona, setPersona] = useState<Persona | null>(null);
  const [tracks, setTracks] = useState<TrackContext[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPersona = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    setError(null);

    const [personaResult, tracksResult] = await Promise.all([api.getPersona(id), api.getPersonaTracks(id)]);

    if (personaResult.error) {
      setError(personaResult.error);
    } else {
      setPersona(personaResult.data?.persona || null);
    }

    if (!tracksResult.error) {
      setTracks(tracksResult.data?.tracks || []);
    }

    setIsLoading(false);
  }, [id]);

  useEffect(() => {
    fetchPersona();
  }, [fetchPersona]);

  return { persona, tracks, isLoading, error, refetch: fetchPersona };
}

export function useCreatePersona() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createPersona = async (data: {
    artist_name: string;
    stage_name?: string;
    creation_method?: string;
    initial_vision?: string;
  }) => {
    setIsLoading(true);
    setError(null);

    const result = await api.createPersona(data);

    setIsLoading(false);

    if (result.error) {
      setError(result.error);
      return null;
    }

    return result.data?.persona;
  };

  return { createPersona, isLoading, error };
}
